<template>
  <el-popover ref="emojiPopover" placement="bottom-start" trigger="click" v-model="popoverVisible">
    <div class="emoji-container">
      <div class="emojiBox unicode-emoji" role="list">
        <button
          type="button"
          class="emoji-item"
          v-for="(item, index) of emojiList"
          :key="index"
          role="listitem"
          :title="item.desc"
          :aria-label="item.desc || item.name"
          @click="chooseEmoji(item.name)">
          <span class="unicode-emoji-text">{{ item.name }}</span>
        </button>
      </div>
    </div>
    <template slot="reference">
      <slot> </slot>
    </template>
  </el-popover>
</template>

<script>
import emojiList from "@/assets/emoji.json";

/**
 * 系统表情选择器。只提供 Unicode 表情，点一下把字符本身交给调用方。
 *
 * 原先这里有第二套「图片表情」分支，靠 chatMode 这个开关切换，默认走图片分支。
 * 两个问题让那条分支从来就没能工作：
 *   1. 图片网格绑的是 item.url，而它和 Unicode 分支共用 emoji.json —— 那份数据
 *      只有 { name, desc }，一个 url 字段都没有，于是整个面板是空白格子，
 *      点一下还会往输入框插一张 src=undefined 的坏图；
 *   2. 另一组「小鸟表情」取自 assets/heo.json，图源 img.yqingke.com 的 DNS
 *      已经解析不到。
 * 而且 handleStick / handleDelete / getEmojiList / uploadSectionFile 调的
 * stickEmoji、deleteEmoji、getEmojiList、addEmoji 四个接口从来没被 import 过，
 * 一旦那条分支被走到就是 ReferenceError。
 *
 * 所以这次把图片分支整个删掉，chatMode 开关也一并去掉：五个调用方里只要有一个
 * 忘了传 chatMode，用户看到的就是一个空面板（/im 页面正是这么坏掉的）。
 * 没有开关，这类 bug 就无法再出现。
 *
 * @emits chooseEmoji 表情字符本身，如 "😀"
 */
export default {
  name: "EmojiPicker",
  data() {
    return {
      emojiList,
      popoverVisible: false,
    };
  },
  methods: {
    chooseEmoji(emoji) {
      this.$emit("chooseEmoji", emoji);
      this.popoverVisible = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.emoji-container {
  max-width: 400px;
  background-color: var(--background-color);
  border: 1px solid var(--border-line);
  padding: 5px;
  border-radius: 5px;

  .emojiBox {
    min-height: 150px;
    max-height: 150px;
    max-width: 400px;
    overflow-y: scroll;

    &::-webkit-scrollbar {
      display: none;
    }

    // Unicode 表情样式
    &.unicode-emoji {
      .emoji-item {
        // 用 button 而不是 span：键盘能 Tab 到、回车能触发，读屏软件也能读出 aria-label。
        // 代价是要把浏览器默认按钮样式清掉。
        display: inline-block;
        padding: 0;
        margin: 0;
        border: none;
        background: none;
        font: inherit;
        color: inherit;
        cursor: pointer;

        .unicode-emoji-text {
          display: inline-block;
          font-size: 28px;
          padding: 4px 6px;
          border-radius: 5px;
          transition: all 0.2s;
        }

        &:hover .unicode-emoji-text,
        &:focus-visible .unicode-emoji-text {
          background-color: rgb(221, 221, 221);
          transform: scale(1.15);
        }

        &:focus {
          outline: none;
        }

        &:focus-visible {
          outline: 2px solid var(--theme-color, #409eff);
          outline-offset: 2px;
        }
      }
    }
  }
}
</style>
